import {
  Dispatch,
  Fragment,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { organisationApi, OrganisationUserManagementStatus, UserRole } from '../../../../services';
import {
  Button,
  ButtonType,
  Check,
  emailValidationError,
  getCssVar,
  IconLabel,
  InputField,
  ListOpenDirection,
  Modal,
  Radio,
  Select,
  TextAreaField,
  useAppSelector,
} from '../../../../shared';

import classNames from 'classnames';
import classes from './AddUsersModal.module.scss';

const CSV_REQUIRED_COLUMNS = 2;

interface AddUsersModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  callback: () => void;
  channelId?: number;
}

enum InviteBy {
  EMAIL = 'EMAIL',
  COMPANY_ID = 'COMPANY_ID',
}

interface UserInvite {
  email: string;
  emailError: boolean;
  companyId: string;
  companyIdError: boolean;
  screenName: string;
  screenNameError: boolean;
  position: string;
  department: string;
  expanded: boolean;
}

export const AddUsersModal: FunctionComponent<AddUsersModalProps> = memo(
  ({ isOpen, setIsOpen, callback, channelId }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsersInviteModal' });

    const [organisationUserInviteByEmail] =
      organisationApi.endpoints.organisationUserInviteByEmail.useMutation();

    const [organisationChannelUserInviteByEmail] =
      organisationApi.endpoints.organisationChannelUserInviteByEmail.useMutation();

    const [organisationUserInviteByCompanyId] =
      organisationApi.endpoints.organisationUserInviteByCompanyId.useMutation();

    const [organisationChannelUserInviteByCompanyId] =
      organisationApi.endpoints.organisationChannelUserInviteByCompanyId.useMutation();

    const [organisationUserInviteByEmailWithGeneratePassword] =
      organisationApi.endpoints.organisationUserInviteByEmailWithGeneratePassword.useMutation();

    const [organisationChannelUserInviteByEmailWithGeneratePassword] =
      organisationApi.endpoints.organisationChannelUserInviteByEmailWithGeneratePassword.useMutation();

    const { channels } = useAppSelector(({ channels }) => channels);

    const [inviteBy, setInviteBy] = useState<InviteBy>(InviteBy.EMAIL);

    const [notifyExistingUsers, setNotifyExistingUsers] = useState<boolean>(false);

    const [role, setRole] = useState<UserRole>(UserRole.CHANNEL_READER);

    const [channelIds, setChannelIds] = useState<number[]>([]);

    const [isMultipleInviteOpen, setIsMultipleInviteOpen] = useState<boolean>(false);

    const [multipleInviteEmails, setMultipleInviteEmails] = useState<string>('');

    const [isFileInviteOpen, setIsFileInviteOpen] = useState<boolean>(false);

    const [includeFileHeaders, setIncludeFileHeaders] = useState<boolean>(true);

    const [inviteLoading, setInviteLoading] = useState<boolean>(false);

    const [inviteWithCodeLoading, setInviteWithCodeLoading] = useState<boolean>(false);

    const fileRef = useRef<HTMLInputElement | null>(null);

    const listRef = useRef<HTMLDivElement | null>(null);

    const isInviteByEmail = useMemo(() => inviteBy === InviteBy.EMAIL, [inviteBy]);

    const userInviteDefault = useMemo(() => {
      return {
        email: '',
        emailError: true,
        companyId: '',
        companyIdError: true,
        screenName: '',
        screenNameError: true,
        position: '',
        department: '',
        expanded: false,
      };
    }, []);

    const [users, setUsers] = useState<UserInvite[]>([{ ...userInviteDefault }]);

    const [fileParsedUsers, setFileParsedUsers] = useState<UserInvite[]>([]);

    const nonEmptyUsers = useMemo(
      () =>
        users.some((user) =>
          Object.values(user).some((value) => typeof value === 'string' && value.trim() !== '')
        ),
      [users]
    );

    const channelDropdownItems = useMemo(
      () =>
        channels.map(({ id, name }) => {
          return { id, title: name };
        }),
      [channels]
    );

    const isNonValidToRequest = useMemo(() => {
      if (
        inviteLoading ||
        inviteWithCodeLoading ||
        (!channelId && !channelIds.length) ||
        users.some(({ screenNameError }) => screenNameError)
      ) {
        return true;
      }

      return isInviteByEmail
        ? users.some(({ emailError }) => emailError)
        : users.some(({ companyIdError }) => companyIdError);
    }, [
      channelId,
      channelIds.length,
      inviteLoading,
      inviteWithCodeLoading,
      isInviteByEmail,
      users,
    ]);

    const onClose = useCallback(() => {
      setIsOpen(false);
      setInviteBy(InviteBy.EMAIL);
      setUsers([{ ...userInviteDefault }]);
      setChannelIds([]);
      setNotifyExistingUsers(false);
      setRole(UserRole.CHANNEL_READER);
    }, [setIsOpen, userInviteDefault]);

    const onMultipleInviteClose = useCallback(() => {
      setIsMultipleInviteOpen(false);
      setMultipleInviteEmails('');
    }, []);

    const onFileInviteClose = useCallback(() => {
      setIsFileInviteOpen(false);
      setIncludeFileHeaders(true);
      setFileParsedUsers([]);
    }, []);

    const usersFieldsToggle = useCallback(
      (index: number) => {
        const usersCopy = [...users];

        usersCopy[index].expanded = !usersCopy[index].expanded;

        setUsers(usersCopy);
      },
      [users]
    );

    const usersFieldsRemove = useCallback(
      (index: number) => {
        const usersCopy = [...users];

        usersCopy.splice(index, 1);

        setUsers(usersCopy);
      },
      [users]
    );

    const emailValidation = useCallback(
      (index: number) => {
        const usersCopy = [...users];

        usersCopy[index].emailError = emailValidationError(usersCopy[index].email);

        setUsers(usersCopy);
      },
      [users]
    );

    const companyIdValidationError = useCallback((companyId: string) => {
      return companyId.trim().length < 1;
    }, []);

    const companyIdValidation = useCallback(
      (index: number) => {
        const usersCopy = [...users];

        usersCopy[index].companyIdError = companyIdValidationError(usersCopy[index].companyId);

        setUsers(usersCopy);
      },
      [companyIdValidationError, users]
    );

    const screenNameValidationError = useCallback((screenName: string) => {
      return screenName.trim().length < 1;
    }, []);

    const screenNameValidation = useCallback(
      (index: number) => {
        const usersCopy = [...users];

        usersCopy[index].screenNameError = screenNameValidationError(usersCopy[index].screenName);

        setUsers(usersCopy);
      },
      [screenNameValidationError, users]
    );

    const onUserFieldChange = useCallback(
      <T extends keyof UserInvite>({
        field,
        value,
        index,
      }: {
        field: T;
        value: UserInvite[T];
        index: number;
      }) => {
        const usersCopy = [...users];

        usersCopy[index][field] = value;

        if (field === 'email') {
          usersCopy[index].emailError = false;
        }

        if (field === 'companyId') {
          usersCopy[index].companyIdError = false;
        }

        if (field === 'screenName') {
          usersCopy[index].screenNameError = false;
        }

        setUsers(usersCopy);
      },
      [users]
    );

    const renderUsersFields = useMemo(() => {
      return (
        <div ref={listRef} className={classes['add-users__list']}>
          {users.map(
            (
              {
                email,
                emailError,
                companyId,
                companyIdError,
                screenName,
                screenNameError,
                position,
                department,
                expanded,
              },
              index
            ) => {
              return (
                <Fragment key={`user-${index}`}>
                  <div className={classes['add-users__list-fields-wrapper']}>
                    <IconLabel
                      iconId={expanded ? 'up-arrow' : 'down-arrow'}
                      ariaLabel={t(`common.${expanded ? 'collapse' : 'expand'}`)}
                      color={getCssVar('--base-link-text-color')}
                      hoverColor={getCssVar('--base-link-text-hover-color')}
                      onClick={() => usersFieldsToggle(index)}
                    />
                    <div className={classes['add-users__list-fields']}>
                      <div className={classes['add-users__list-fields-row']}>
                        {isInviteByEmail && (
                          <InputField
                            placeholder={`${t('common.email')} *`}
                            value={email}
                            onChange={({ target: { value } }) =>
                              onUserFieldChange({ field: 'email', value, index })
                            }
                            onBlur={() => emailValidation(index)}
                            error={Boolean(email.length && emailError)}
                          />
                        )}
                        {inviteBy === InviteBy.COMPANY_ID && (
                          <InputField
                            placeholder={`${t('common.company-id')} *`}
                            value={companyId}
                            onChange={({ target: { value } }) =>
                              onUserFieldChange({ field: 'companyId', value, index })
                            }
                            onBlur={() => companyIdValidation(index)}
                            error={Boolean(companyId.length && companyIdError)}
                          />
                        )}
                        <InputField
                          placeholder={`${t('common.name')} *`}
                          value={screenName}
                          onChange={({ target: { value } }) =>
                            onUserFieldChange({ field: 'screenName', value, index })
                          }
                          onBlur={() => screenNameValidation(index)}
                          error={Boolean(screenName.length && screenNameError)}
                        />
                      </div>
                      {expanded && (
                        <div className={classes['add-users__list-fields-row']}>
                          <InputField
                            placeholder={t('common.position')}
                            value={position}
                            onChange={({ target: { value } }) =>
                              onUserFieldChange({ field: 'position', value, index })
                            }
                          />
                          <InputField
                            placeholder={t('common.department')}
                            value={department}
                            onChange={({ target: { value } }) =>
                              onUserFieldChange({ field: 'department', value, index })
                            }
                          />
                        </div>
                      )}
                    </div>
                    {users.length > 1 && (
                      <IconLabel
                        iconId={'delete'}
                        iconSize={20}
                        ariaLabel={t('common.remove')}
                        color={getCssVar('--color-danger')}
                        hoverColor={getCssVar('--color-danger-hover')}
                        onClick={() => usersFieldsRemove(index)}
                      />
                    )}
                  </div>
                </Fragment>
              );
            }
          )}
        </div>
      );
    }, [
      companyIdValidation,
      emailValidation,
      inviteBy,
      isInviteByEmail,
      onUserFieldChange,
      screenNameValidation,
      t,
      users,
      usersFieldsRemove,
      usersFieldsToggle,
    ]);

    const scrollListToBottom = useCallback(() => {
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }, 0);
    }, []);

    const addAnotherUser = useCallback(() => {
      setUsers([...users, { ...userInviteDefault }]);
      scrollListToBottom();
    }, [scrollListToBottom, userInviteDefault, users]);

    const inviteByEmailUsersMap = useCallback(() => {
      return users.map(({ email, screenName, position, department }) => {
        return { email, screenName, position, department };
      });
    }, [users]);

    const inviteByCompanyIdUsersMap = useCallback(() => {
      return users.map(({ companyId, screenName, position, department }) => {
        return { companyId, screenName, position, department };
      });
    }, [users]);

    const inviteHandler = useCallback(async () => {
      setInviteLoading(true);

      const request = isInviteByEmail
        ? channelId
          ? organisationChannelUserInviteByEmail({
              channelId,
              users: inviteByEmailUsersMap(),
              role,
              notifyExistingUsers,
            })
          : organisationUserInviteByEmail({
              channelIds,
              users: inviteByEmailUsersMap(),
              role,
              notifyExistingUsers,
            })
        : channelId
        ? organisationChannelUserInviteByCompanyId({
            channelId,
            users: inviteByCompanyIdUsersMap(),
            role,
          })
        : organisationUserInviteByCompanyId({
            channelIds,
            users: inviteByCompanyIdUsersMap(),
            role,
          });

      try {
        const userManagementResult = await toast.promise(request.unwrap(), {
          pending: ctxT('request-pending'),
        });

        if (userManagementResult.length) {
          const hasSkipOrAccess = userManagementResult.find(({ status }) =>
            [
              OrganisationUserManagementStatus.SKIP,
              OrganisationUserManagementStatus.ACCESS,
            ].includes(status)
          );

          hasSkipOrAccess
            ? toast.info(ctxT('invite-skip'))
            : toast.success(ctxT('request-success'));

          callback();
          onClose();
        }
      } catch (e) {
        const error = e as { status?: string; originalStatus?: number };

        if (error.status === 'FETCH_ERROR' || error.originalStatus === 504) {
          toast.success(ctxT('request-success'));
          callback();
          onClose();
          return;
        }

        toast.error(ctxT('request-error'));
      } finally {
        setInviteLoading(false);
      }
    }, [
      callback,
      channelId,
      channelIds,
      ctxT,
      inviteByCompanyIdUsersMap,
      inviteByEmailUsersMap,
      isInviteByEmail,
      notifyExistingUsers,
      onClose,
      organisationChannelUserInviteByCompanyId,
      organisationChannelUserInviteByEmail,
      organisationUserInviteByCompanyId,
      organisationUserInviteByEmail,
      role,
    ]);

    const inviteByEmailWithCode = useCallback(async () => {
      setInviteWithCodeLoading(true);

      const request = channelId
        ? organisationChannelUserInviteByEmailWithGeneratePassword({
            channelId,
            users: inviteByEmailUsersMap(),
            notifyExistingUsers,
            role,
          })
        : organisationUserInviteByEmailWithGeneratePassword({
            channelIds,
            users: inviteByEmailUsersMap(),
            notifyExistingUsers,
            role,
          });

      try {
        const userManagementResult = await toast.promise(request.unwrap(), {
          pending: ctxT('request-pending'),
          error: ctxT('request-error'),
        });

        if (userManagementResult.length) {
          const hasSkipOrAccess = userManagementResult.find(({ status }) =>
            [
              OrganisationUserManagementStatus.SKIP,
              OrganisationUserManagementStatus.ACCESS,
            ].includes(status)
          );

          hasSkipOrAccess
            ? toast.info(ctxT('invite-skip'))
            : toast.success(ctxT('request-success'));

          callback();
          onClose();
        }
      } catch (_) {
      } finally {
        setInviteWithCodeLoading(false);
      }
    }, [
      callback,
      channelId,
      channelIds,
      ctxT,
      inviteByEmailUsersMap,
      notifyExistingUsers,
      onClose,
      organisationChannelUserInviteByEmailWithGeneratePassword,
      organisationUserInviteByEmailWithGeneratePassword,
      role,
    ]);

    const title = ctxT('title');

    const body = useMemo(() => {
      return (
        <div
          className={classNames(classes['add-users'], {
            [classes['add-users--loading']]: inviteLoading || inviteWithCodeLoading,
          })}
        >
          <div className={classes['add-users__title']}>{title}</div>
          <div className={classes['add-users__invite-by']}>
            <div className={classes['add-users__invite-by-label']}>{ctxT('invite-by')}</div>
            {Object.values(InviteBy).map((value) => {
              return (
                <Radio
                  key={value}
                  checked={value === inviteBy}
                  name={'invite-by'}
                  label={ctxT(`invite-by-${value}`)}
                  onChange={() => setInviteBy(value)}
                />
              );
            })}
          </div>

          {renderUsersFields}

          <div className={classes['add-users__invite-links']}>
            <IconLabel
              label={ctxT('add-another-user')}
              onClick={addAnotherUser}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
            />

            <IconLabel
              label={ctxT('add-multiple-users')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={() => setIsMultipleInviteOpen(true)}
              disabled={!isInviteByEmail}
            />

            <IconLabel
              label={ctxT('add-from-file')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={() => setIsFileInviteOpen(true)}
            />
          </div>

          <div className={classes['add-users__role']}>
            <div className={classes['add-users__role-label']}>{t('common.role')}</div>
            {Object.values(UserRole)
              .filter((value) => value.startsWith('CHANNEL_'))
              .map((value) => {
                return (
                  <Radio
                    key={value}
                    checked={value === role}
                    name={'invite-role'}
                    label={t(`userRoles.${value}`)}
                    onChange={() => setRole(value)}
                  />
                );
              })}
          </div>

          {!channelId && (
            <Select
              label={t('common.channel')}
              selectedItemIds={channelIds}
              items={channelDropdownItems}
              onMultiCheckChange={setChannelIds}
              listOpenDirection={ListOpenDirection.top}
              listMaxHeightRem={16}
            />
          )}

          {isInviteByEmail && (
            <Check
              label={ctxT('notify-existing-users')}
              checked={notifyExistingUsers}
              onChange={setNotifyExistingUsers}
            />
          )}

          <div className={classes['add-users__buttons']}>
            <Button type={ButtonType.secondary} label={t('common.cancel')} onClick={onClose} />

            {isInviteByEmail &&
              [UserRole.CHANNEL_READER, UserRole.CHANNEL_EDITOR_LIMITED].includes(role) && (
                <Button
                  type={ButtonType.primary}
                  label={ctxT('generate-new-code')}
                  disabled={isNonValidToRequest}
                  onClick={inviteByEmailWithCode}
                  loading={inviteWithCodeLoading}
                />
              )}

            <Button
              type={ButtonType.primary}
              label={ctxT(isInviteByEmail ? 'send-invites' : 'add-users')}
              disabled={isNonValidToRequest}
              onClick={inviteHandler}
              loading={inviteLoading}
            />
          </div>
        </div>
      );
    }, [
      addAnotherUser,
      channelDropdownItems,
      channelId,
      channelIds,
      ctxT,
      inviteBy,
      inviteByEmailWithCode,
      inviteHandler,
      inviteLoading,
      inviteWithCodeLoading,
      isInviteByEmail,
      isNonValidToRequest,
      notifyExistingUsers,
      onClose,
      renderUsersFields,
      role,
      t,
      title,
    ]);

    const multipleInviteEmailsParse = useCallback(() => {
      onMultipleInviteClose();

      const emails = multipleInviteEmails
        .split(',')
        .map((email) => email.trim())
        .filter((email) => !emailValidationError(email));

      if (!emails.length) {
        return;
      }

      const newUsers = emails.map((email) => {
        return {
          ...userInviteDefault,
          email,
          emailError: false,
          screenName: email.split('@')[0],
          screenNameError: false,
        };
      });

      if (nonEmptyUsers) {
        setUsers([...users, ...newUsers]);
        scrollListToBottom();
        return;
      }

      setUsers([...newUsers]);
    }, [
      multipleInviteEmails,
      nonEmptyUsers,
      onMultipleInviteClose,
      scrollListToBottom,
      userInviteDefault,
      users,
    ]);

    const multipleInviteTitle = ctxT('add-multiple-users');

    const multipleInviteBody = useMemo(() => {
      return (
        <div className={classes['add-users']}>
          <div className={classes['add-users__title']}>{multipleInviteTitle}</div>
          <TextAreaField
            label={ctxT('add-multiple-users-label')}
            value={multipleInviteEmails}
            onChange={({ target: { value } }) => setMultipleInviteEmails(value)}
            placeholder={ctxT('add-multiple-users-placeholder')}
            minRows={3}
          />
          <div className={classes['add-users__buttons']}>
            <Button
              type={ButtonType.secondary}
              label={t('common.cancel')}
              onClick={onMultipleInviteClose}
            />
            <Button
              type={ButtonType.primary}
              label={t('common.parse')}
              onClick={multipleInviteEmailsParse}
            />
          </div>
        </div>
      );
    }, [
      ctxT,
      multipleInviteEmails,
      multipleInviteEmailsParse,
      multipleInviteTitle,
      onMultipleInviteClose,
      t,
    ]);

    const parseCSV = useCallback(
      (text: string) => {
        const rows = text
          .trim()
          .split('\n')
          .slice(includeFileHeaders ? 1 : 0)
          .map((line) => line.split(',').map((value) => value.trim()));

        if (rows.some(({ length }) => length < CSV_REQUIRED_COLUMNS)) {
          return null;
        }

        return rows.map(([identifier, name, position = '', department = '']) => {
          return {
            email: isInviteByEmail ? identifier : '',
            companyId: !isInviteByEmail ? identifier : '',
            name,
            position,
            department,
          };
        });
      },
      [includeFileHeaders, isInviteByEmail]
    );

    const onFileChange = useCallback(
      async (files: FileList | File[] | null) => {
        if (!files?.length) {
          return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
          const text = event.target?.result;

          if (!text) {
            return;
          }

          const newUsers = parseCSV(text as string);

          if (!newUsers?.length) {
            toast.error(ctxT('file-format-error'));
            return;
          }

          setFileParsedUsers(
            newUsers.map(({ email, companyId, name, position, department }) => {
              return {
                email,
                emailError: emailValidationError(email),
                companyId,
                companyIdError: companyIdValidationError(companyId),
                screenName: name,
                screenNameError: screenNameValidationError(name),
                position,
                department,
                expanded: false,
              };
            })
          );
        };

        reader.readAsText(files[0]);
      },
      [companyIdValidationError, ctxT, parseCSV, screenNameValidationError]
    );

    const onFileParsedUsersSave = useCallback(() => {
      onFileInviteClose();

      if (nonEmptyUsers) {
        setUsers([...users, ...fileParsedUsers]);
        scrollListToBottom();
        return;
      }

      setUsers([...fileParsedUsers]);
    }, [fileParsedUsers, nonEmptyUsers, onFileInviteClose, scrollListToBottom, users]);

    const fileInviteTitle = ctxT('add-from-file');

    const fileInviteBody = useMemo(() => {
      return (
        <div className={classes['add-users']}>
          <div className={classes['add-users__title']}>{fileInviteTitle}</div>
          <div className={classes['add-users__onboard-by']}>
            <div className={classes['add-users__onboard-by-label']}>{ctxT('onboard-method')}</div>
            {Object.values(InviteBy).map((value) => {
              return (
                <Radio
                  key={value}
                  checked={value === inviteBy}
                  name={'onboard-by'}
                  label={ctxT(`onboard-by-${value}`)}
                  onChange={() => setInviteBy(value)}
                />
              );
            })}
          </div>

          <div>
            {ctxT('file-fields-order')}: '
            {t(`common.${isInviteByEmail ? 'email' : 'company-id'}`).toLowerCase()}', '
            {t('common.name').toLowerCase()}', '{t('common.position').toLowerCase()}', '
            {t('common.department').toLowerCase()}'
          </div>

          <div className={classes['add-users__upload']}>
            <div className={classes['add-users__upload-intro']}>{ctxT('upload-intro')}</div>

            <IconLabel
              iconId={'file'}
              label={ctxT('download-sample-file')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={() =>
                window.open(
                  `/webapp/assets/template_onboarding_via_${
                    isInviteByEmail ? 'email' : 'company_ID'
                  }.csv`,
                  '_blank',
                  'noopener, noreferrer'
                )
              }
              labelFirst
            />

            <input
              type={'file'}
              ref={fileRef}
              accept={'.csv'}
              className={classes['add-users__upload-file']}
              onChange={({ target: { files } }) => onFileChange(files)}
              onClick={({ target }) => ((target as HTMLInputElement).value = '')}
            />

            <Check
              label={ctxT('include-file-headers')}
              checked={includeFileHeaders}
              onChange={setIncludeFileHeaders}
            />

            <Button
              type={ButtonType.primary}
              label={ctxT('upload-csv')}
              className={classes['add-users__upload-button']}
              onClick={() => fileRef.current?.click()}
            />

            {Boolean(fileParsedUsers.length) && (
              <div className={classes['add-users__upload-table-wrapper']}>
                <table className={classes['add-users__upload-table']}>
                  <thead>
                    <tr>
                      <th>{t(`common.${isInviteByEmail ? 'email' : 'company-id'}`)}</th>
                      <th>{t('common.name')}</th>
                      <th>{t('common.position')}</th>
                      <th>{t('common.department')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileParsedUsers.map(
                      ({ email, companyId, screenName, position, department }, index) => {
                        return (
                          <tr key={index}>
                            <td>{isInviteByEmail ? email : companyId}</td>
                            <td className={classes['add-users__upload-table-name']}>
                              {screenName}
                            </td>
                            <td>{position}</td>
                            <td>{department}</td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {Boolean(fileParsedUsers.length) && (
              <>
                <span className={classes['add-users__upload-total']}>
                  {t('common.total')}: {fileParsedUsers.length}
                </span>
                <Button
                  type={ButtonType.primary}
                  label={t('common.save')}
                  className={classes['add-users__upload-save']}
                  onClick={onFileParsedUsersSave}
                />
              </>
            )}
          </div>
        </div>
      );
    }, [
      ctxT,
      fileInviteTitle,
      fileParsedUsers,
      includeFileHeaders,
      inviteBy,
      isInviteByEmail,
      onFileChange,
      onFileParsedUsersSave,
      t,
    ]);

    return (
      <>
        <Modal
          isOpen={isOpen}
          body={body}
          onClose={onClose}
          keepOpened={inviteLoading || inviteWithCodeLoading}
          ariaLabel={title}
          disableCloseByOverlay
          alignTop
        />

        <Modal
          isOpen={isMultipleInviteOpen}
          body={multipleInviteBody}
          onClose={onMultipleInviteClose}
          contentStyle={{ top: '3rem', maxWidth: '40rem' }}
          ariaLabel={multipleInviteTitle}
          alignTop
        />

        <Modal
          isOpen={isFileInviteOpen}
          body={fileInviteBody}
          onClose={onFileInviteClose}
          contentStyle={{ top: '3rem', maxWidth: '40rem' }}
          ariaLabel={fileInviteTitle}
          alignTop
        />
      </>
    );
  }
);
